// Phase 2 "fast media": press-and-hold on a phone plays a card's silent preview (a mouse just hovers). Plain logic, no
// DOM, so it's unit-tested in holdpreview.test.ts; components/PosterPreview.tsx wires it to pointer events.
//   - a press that's held for HOLD_MS starts the preview; lifting the finger stops it
//   - moving more than MOVE_TOLERANCE_PX before then is a scroll, not a hold: nothing plays
//   - the click that follows a hold is swallowed (holding a card to peek doesn't open it); a quick tap still opens it

export const HOLD_MS = 280;
export const MOVE_TOLERANCE_PX = 10;

export type Timers = { set: (f: () => void, ms: number) => unknown; clear: (handle: unknown) => void };
const realTimers: Timers = { set: (f, ms) => setTimeout(f, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) };

export class HoldGesture {
  private timer: unknown = null;
  private start: { x: number; y: number } | null = null;
  private swallow = false;
  /** True while the preview is playing because of a hold. */
  held = false;

  constructor(
    private onLive: (live: boolean) => void,
    private timers: Timers = realTimers,
  ) {}

  down(x: number, y: number) {
    this.reset();
    this.swallow = false;
    this.start = { x, y };
    this.timer = this.timers.set(() => {
      this.timer = null;
      this.held = true;
      this.swallow = true;
      this.onLive(true);
    }, HOLD_MS);
  }

  move(x: number, y: number) {
    if (!this.start || this.held) return;
    if (Math.hypot(x - this.start.x, y - this.start.y) > MOVE_TOLERANCE_PX) this.cancel();
  }

  up() {
    this.reset();
  }

  /** The browser took the touch over (a scroll), or the pointer left. */
  cancel() {
    this.reset();
    this.swallow = false;
  }

  /** Call from the click handler: true when this click ends a hold and should be ignored (only once). */
  takeClick(): boolean {
    const s = this.swallow;
    this.swallow = false;
    return s;
  }

  private reset() {
    if (this.timer !== null) this.timers.clear(this.timer);
    this.timer = null;
    this.start = null;
    if (this.held) {
      this.held = false;
      this.onLive(false);
    }
  }
}
